import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../api";
import { todayISO } from "../utils/format";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export default function FuelDialog({ vehicleId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ vehicle_id: vehicleId || "", date: todayISO(), mileage: "", liters: "", price_per_liter: "", total: "", station: "", driver: "", account_id: "" });

  useEffect(() => {
    if (!open) return;
    if (!vehicleId) api.get("/vehicles").then((r) => setVehicles(r.data.filter((v) => v.status !== "Vendu")));
    api.get("/accounts").then((r) => setAccounts(r.data));
    setForm((f) => ({ ...f, vehicle_id: vehicleId || "" }));
  }, [open, vehicleId]);

  const set = (k, v) => setForm((f) => {
    const next = { ...f, [k]: v };
    if (k === "liters" || k === "price_per_liter") {
      const t = (Number(next.liters) || 0) * (Number(next.price_per_liter) || 0);
      next.total = t ? String(Math.round(t)) : "";
    }
    return next;
  });

  const submit = async () => {
    if (!form.vehicle_id || !form.liters) return toast.error("Véhicule et litres requis");
    try {
      await api.post("/fuel", { ...form, liters: Number(form.liters), price_per_liter: Number(form.price_per_liter) || 0, total: Number(form.total) || 0, mileage: form.mileage ? Number(form.mileage) : null, account_id: form.account_id || null });
      toast.success("Ravitaillement enregistré — ajouté au coût du véhicule");
      setOpen(false);
      onSaved?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-fuel-button"><Plus className="mr-1 h-4 w-4" /> Ravitaillement</Button></DialogTrigger>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nouveau ravitaillement carburant</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          {!vehicleId && (
            <div className="col-span-2"><Label>Véhicule *</Label>
              <Select value={form.vehicle_id} onValueChange={(v) => set("vehicle_id", v)}>
                <SelectTrigger data-testid="fuel-vehicle-select"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                <SelectContent>{vehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.internal_number} — {v.brand} {v.model}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="fuel-date" /></div>
          <div><Label>Kilométrage</Label><Input type="number" value={form.mileage} onChange={(e) => set("mileage", e.target.value)} data-testid="fuel-mileage" /></div>
          <div><Label>Litres *</Label><Input type="number" step="0.1" value={form.liters} onChange={(e) => set("liters", e.target.value)} data-testid="fuel-liters" /></div>
          <div><Label>Prix / litre</Label><Input type="number" value={form.price_per_liter} onChange={(e) => set("price_per_liter", e.target.value)} data-testid="fuel-price" /></div>
          <div><Label>Montant total (auto)</Label><Input type="number" value={form.total} onChange={(e) => set("total", e.target.value)} data-testid="fuel-total" /></div>
          <div><Label>Station</Label><Input value={form.station} onChange={(e) => set("station", e.target.value)} data-testid="fuel-station" /></div>
          <div><Label>Conducteur</Label><Input value={form.driver} onChange={(e) => set("driver", e.target.value)} /></div>
          <div><Label>Compte débité</Label>
            <Select value={form.account_id} onValueChange={(v) => set("account_id", v === "none" ? "" : v)}>
              <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Aucun</SelectItem>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} data-testid="fuel-submit">Enregistrer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
